import { useState, useEffect, useRef, useCallback } from 'react';
import { createWebSocket, WebSocketRequest } from '@/utils/request';
import { useLocale } from './useLocale';

// Define the notification data structure based on backend response
export interface NotificationData {
  user_id: string;
  amount: string;
}

export interface NotificationWebSocketMessage {
  msg_id?: number;
  action?: string;
  topic?: string;
  code?: number;
  ts?: number;
  data?: NotificationData[];
  // Also allow for different possible field variations
  Action?: string;
  Topic?: string;
  [key: string]: any; // Allow additional fields
}

// Custom hook for notification WebSocket
export const useNotificationWebSocket = () => {
  const { t } = useLocale();
  const [notifications, setNotifications] = useState<string[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocketRequest | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const lastConnectionAttempt = useRef<number>(0);
  const connectionThrottleMs = 30000; // 30 seconds minimum between connection attempts

  // Convert backend data to display messages
  const formatNotifications = useCallback((data: NotificationData[]) => {
    return data.map(item => 
      t('overview.notifications.userReward')
        .replace('{id}', item.user_id)
        .replace('{amount}', parseFloat(item.amount).toFixed(2))
    );
  }, [t]);

  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.isConnected()) {
      return;
    }

    // Throttle connection attempts to prevent too frequent connections
    const now = Date.now();
    if (now - lastConnectionAttempt.current < connectionThrottleMs) {
      console.log('Connection throttled, waiting...');
      return;
    }
    lastConnectionAttempt.current = now;

    try {
      const wsUrl = import.meta.env.VITE_WS_URL;
      if (!wsUrl) {
        console.error('WebSocket URL not configured');
        return;
      }

      // Build the full WebSocket URL - handle different URL formats
      let fullWsUrl = wsUrl;
      if (!wsUrl.endsWith('/')) {
        fullWsUrl += '/';
      }
      fullWsUrl += '';

      console.log('Connecting to WebSocket...');

      wsRef.current = createWebSocket({
        url: fullWsUrl,
        reconnectAttempts: 3, // Reduced from 5
        reconnectInterval: 10000, // Increased from 3000ms to 10 seconds
        heartbeatInterval: 60000, // Increased from 30000ms to 1 minute
        heartbeatMessage: JSON.stringify({ action: 'ping' }),
        
        onOpen: (event) => {
          console.log('Notification WebSocket connected successfully');
          setIsConnected(true);
          
          // Subscribe to carousel notifications - using backend's expected format
          const subscribeMessage = {
            Action: 'sub',
            Topic: 'affiliate.carousel'
          };
          console.log('Subscribing to affiliate.carousel');
          wsRef.current?.send(JSON.stringify(subscribeMessage));
        },

        onMessage: (event) => {
          try {
            const message: NotificationWebSocketMessage = JSON.parse(event.data);
            
            // Check for both possible field name formats
            const topic = message.topic || message.Topic;
            const action = message.action || message.Action;
            const code = message.code;
            const data = message.data;
            
            // Only log important messages to prevent excessive console output
            if (topic === 'affiliate.carousel' && action === 'notify') {
              console.log('Affiliate carousel notification received:', message);
              
              if (code === 0 && data && Array.isArray(data)) {
                const formattedNotifications = formatNotifications(data);
                setNotifications(formattedNotifications);
                console.log('Updated notifications count:', formattedNotifications.length);
              }
            }
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        },

        onError: (event) => {
          console.error('Notification WebSocket error');
          setIsConnected(false);
        },

        onClose: (event) => {
          console.log('Notification WebSocket disconnected');
          setIsConnected(false);
          
          // Auto reconnect after 15 seconds if not manually closed (increased delay)
          if (!event.wasClean) {
            reconnectTimeoutRef.current = setTimeout(() => {
              console.log('Reconnecting WebSocket...');
              connectWebSocket();
            }, 15000); // Increased from 5000ms to 15 seconds
          }
        }
      });

      wsRef.current.connect();
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setIsConnected(false);
    }
  }, [formatNotifications]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setIsConnected(false);
  }, []);

  useEffect(() => {
    connectWebSocket();

    return () => {
      disconnect();
    };
  }, [connectWebSocket, disconnect]);

  // Initialize fallback data on mount
  useEffect(() => {
    // Use fallback mock data initially
    const fallbackNotifications = [
      t('overview.notifications.userReward').replace('{id}', '350***958').replace('{amount}', '12.96'),
      t('overview.notifications.userReward').replace('{id}', '351***739').replace('{amount}', '14.40'),
      t('overview.notifications.userReward').replace('{id}', '353***761').replace('{amount}', '77.78'),
      t('overview.notifications.userReward').replace('{id}', '300***545').replace('{amount}', '0.33'),
      t('overview.notifications.userReward').replace('{id}', '301***472').replace('{amount}', '49.13'),
      t('overview.notifications.userReward').replace('{id}', '304***457').replace('{amount}', '6.11'),
    ];
    setNotifications(fallbackNotifications);
  }, [t]); // Only depend on t function

  return {
    notifications,
    isConnected,
    reconnect: connectWebSocket,
    disconnect
  };
};